import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Pitch } from './types';
import { artistsApi } from '../../repositories/artists';
import { formatDisplayDate } from '../../DashboardPage/utils/dates';
import { Copy, Check } from 'lucide-react';
import {
  XMarkIcon, FolderIcon
} from "@heroicons/react/24/outline";
import Swal from 'sweetalert2';
import Pagination from '../common/Pagination';
import DataTable, { DataTableColumn } from '../ui/DataTable';
import Button from '../ui/Button';

interface ArtistPitchesTabProps {
  artistId: string;
}

interface ExtendedPitch extends Pitch {
  releaseDate: string;
  userId: number;
  trackISRC: string;
  mainGenre?: string;
  configuration?: string;
}

const ArtistPitchesTab: React.FC<ArtistPitchesTabProps> = ({ artistId }) => {
  const [pitches, setPitches] = useState<ExtendedPitch[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // The old footer counted rows on the current page, so it always read
  // "of 10". This holds the real server-side total.
  const [totalResults, setTotalResults] = useState(0);
  const [copiedFields, setCopiedFields] = useState<{ [key: string]: boolean }>({});
  const pageSize = 10;
  // Search 
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState<"artistName" | "releaseTitle">("artistName");

  useEffect(() => {
    loadPitches();
  }, [artistId, currentPage]);

  const loadPitches = async () => {
    try {
      setLoading(true);
      const response = await artistsApi.getPitches(artistId, {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        searchType: searchType
      });

      const data = response.data;
      const pitchesData = data.data || data.pitches || [];

      // Map backend response to frontend format
      const mappedPitches: ExtendedPitch[] = pitchesData.map((pitch: any) => ({
        id: pitch.id,
        artistName: pitch.artistName,
        songTitle: pitch.songTitle,
        releaseTitle: pitch.releaseTitle || pitch.songTitle,
        releaseDate: pitch.releaseDate,
        userId: pitch.userId,
        trackISRC: pitch.trackISRC,
        mainGenre: pitch.mainGenre,
        configuration: pitch.configuration,
        status: pitch.status,
        submittedAt: pitch.submittedAt ? new Date(pitch.submittedAt) : undefined,
        createdAt: new Date(pitch.createdAt),
      }));

      // Sort by release date (newest first)
      const sortedPitches = mappedPitches.sort(
        (a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
      );

      setPitches(sortedPitches);
      const total = data.total || data.totalCount || mappedPitches.length;
      setTotalResults(total);
      setTotalPages(Math.ceil(total / pageSize));

    } catch (error: any) {
      console.error('Error loading pitches:', error);

      // Check if it's a 404 (no pitches found) - this is normal
      if (error.response?.status === 404) {
        setPitches([]);
        setTotalResults(0);
        setTotalPages(1);
      } else {
        // Show error for other types of errors
        const errorMessage = error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to load pitches. Please try again.';

        Swal.fire({
          title: 'Error',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Add search handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchType(e.target.value as "artistName" | "releaseTitle");
  };

  const clearSearch = () => {
    setSearchTerm("");

  };
  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadPitches();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchType]);


  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const copyToClipboard = async (text: string, fieldKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedFields((prev) => ({ ...prev, [fieldKey]: true }));
      setTimeout(() => {
        setCopiedFields((prev) => ({ ...prev, [fieldKey]: false }));
      }, 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  // Categorical tag, shared by genre / configuration / "No ISRC".
  const tagClass =
    "inline-flex items-center px-2.5 py-0.5 text-xs font-medium border rounded-full bg-neutral-100 text-neutral-700 border-neutral-200";

  const pitchColumns: DataTableColumn<ExtendedPitch>[] = [
    {
      key: "artist",
      header: "Artist",
      cell: (pitch) => <span className="font-medium">{pitch.artistName}</span>,
    },
    {
      key: "release",
      header: "Release",
      cell: (pitch) => (
        <div className="max-w-xs font-medium truncate" title={pitch.releaseTitle}>
          {pitch.releaseTitle}
        </div>
      ),
    },
    {
      key: "releaseDate",
      header: "Release Date",
      cell: (pitch) => (
        <span className="text-neutral-600">{formatDisplayDate(pitch.releaseDate)}</span>
      ),
    },
    {
      key: "genre",
      header: "Genre",
      cell: (pitch) => (
        <div className="flex gap-1 flex-nowrap">
          {pitch.mainGenre ? (
            pitch.mainGenre.split(",").map((genre, genreIndex) => (
              <span key={genreIndex} className={tagClass}>
                {genre.trim()}
              </span>
            ))
          ) : (
            <span className={tagClass}>Not specified</span>
          )}
        </div>
      ),
    },
    {
      key: "configuration",
      header: "Configuration",
      cell: (pitch) => (
        <span className={tagClass}>{pitch.configuration || "Not specified"}</span>
      ),
    },
    {
      key: "isrc",
      header: "ISRC",
      cell: (pitch) =>
        pitch.trackISRC ? (
          <div className="flex items-center">
            <div
              className="font-mono max-w-[120px] truncate"
              title={pitch.trackISRC}
            >
              {truncateText(pitch.trackISRC, 12)}
            </div>
            <button
              onClick={() => copyToClipboard(pitch.trackISRC, `isrc-${pitch.id}`)}
              className="border border-transparent p-1 ml-2 transition-colors rounded text-neutral-400 hover:text-primary-700 focus:outline-none focus-visible:border-primary-700"
              title="Copy ISRC"
            >
              {copiedFields[`isrc-${pitch.id}`] ? (
                <Check size={16} className="text-success-600" />
              ) : (
                <Copy size={16} />
              )}
            </button>
          </div>
        ) : (
          <span className={tagClass}>No ISRC</span>
        ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "center",
      cell: (pitch) => (
        <div className="flex items-center justify-center gap-2">
          <Button asChild variant="secondary" size="sm">
            <Link to={`/pitch/submission/${pitch.id}`}>Review Pitch</Link>
          </Button>
          {pitch.trackISRC && (
            <Button asChild variant="secondary" size="sm">
              <Link to={`/analytics/detail/${pitch.id}`}>Analytics</Link>
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Always show search section */}
      {/*
        Search type + term as one control, matching /dashboard and
        /past-submissions: a single border and focus treatment wrap both, with
        an inset divider between them.
      */}
      <div className="flex items-stretch w-full bg-white border rounded-lg border-neutral-300 focus-within:border-primary-500 sm:w-auto">
        <div className="relative shrink-0">
          <select
            value={searchType}
            onChange={handleSearchTypeChange}
            aria-label="Search pitches by field"
            className="w-[104px] sm:w-auto h-full py-2 pl-3 text-sm truncate bg-transparent border-0 appearance-none cursor-pointer pr-7 text-neutral-600 focus:outline-none focus:ring-0"
          >
            <option value="artistName">Artist Name</option>
            <option value="releaseTitle">Release Name</option>
          </select>
          <svg className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-2 top-1/2 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        <span aria-hidden="true" className="self-stretch w-px my-1.5 bg-neutral-200" />

        <div className="relative flex-1 min-w-0 sm:w-64">
          <svg className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={`Search by ${searchType === "artistName" ? "artist name" : searchType === "releaseTitle" ? "release name" : "company name"}...`}
            className="w-full py-2 text-sm text-black bg-transparent border-0 rounded-r-lg pl-9 pr-9 placeholder-neutral-400 focus:outline-none focus:ring-0"
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              aria-label="Clear search"
              className="border border-transparent absolute p-0.5 -translate-y-1/2 rounded right-2 top-1/2 text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/*
        Result count only - the × inside the search input already clears the
        term, so a second "Clear search" here would be two controls for one
        piece of state.
      */}
      {searchTerm && !loading && (
        <div className="p-3 border rounded-lg border-primary-200 bg-primary-50">
          <span className="text-sm text-primary-900">
            Found {pitches.length} result{pitches.length !== 1 ? 's' : ''} for "{searchTerm}" in {searchType === "artistName" ? "artist name" : searchType === "releaseTitle" ? "release name" : "company name"}.
          </span>
        </div>
      )}

      {/*
        Conditional content based on assets and search. The loading check comes
        first: while the fetch is in flight `pitches` is empty, which would
        otherwise read as "no results for this search" before any result exists.
      */}
      {!loading && pitches.length === 0 && searchTerm ? (

        // No search results found
        <div className="py-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 text-neutral-400">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <p className="text-neutral-500">
            No pitches found matching "{searchTerm}" in {searchType === "artistName" ? "artist name" : searchType === "releaseTitle" ? "release name" : "company name"}.
          </p>
          <Button variant="secondary" onClick={clearSearch} className="mt-4">
            <XMarkIcon className="w-4 h-4" />
            Clear search
          </Button>
        </div>
      ) : (

        <div>
          <DataTable
            columns={pitchColumns}
            rows={pitches}
            getRowKey={(pitch: ExtendedPitch) => pitch.id}
            loading={loading}
            skeletonRows={6}
            emptyMessage="No pitches found."
          />

          {/* Page counts aren't known until the fetch lands. */}
          {!loading && (
            <div className="mt-3">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                setPage={setCurrentPage}
                totalItems={totalResults}
                limit={pageSize}
              />
            </div>
          )}
        </div>)}
    </div>
  );
};

export default ArtistPitchesTab;